import { useState, useEffect } from 'react';
import { useParams, useHistory } from 'react-router-dom';
import { toastWarning } from '../../../components/popups/Toast';
import Page, { PageContentBanner, PageContentBodyGrid } from '../../Shared/Page/Page';
import PageGridItem from '../../Shared/Page/PageGridItem';
import useWindowDimensions, { useRemote, useStorage } from '../../../Utils/Utils';
import TemplateGeneralDetails from './TemplateGeneralDetails';
import PageGridDivider from '../../Shared/Page/PageGridDivider';
import ActionButtonsBanner from '../../Shared/Banner/ActionButtonsBanner';
import TemplateSettingsList from './TemplateSettingsList'
import TemplateOptions from './TemplateOptions';
import MiniSectorsList from '../../Shared/TablesAndLists/MiniSectorsList';
import AssociatedFundsList from './AssociatedFundsList';


const TemplateMgmt = ({  }) => {
	const [template, setTemplate] = useState({});
	const [originalTemplate, setOriginalTemplate] = useState({})
	const [selectedPublishActions, setSelectedPublishActions] = useState([]);
	const [originalSelectedActions, setOriginalSelectedActions] = useState([])
	const [fundsUsed, setFundsUsed] = useState([])
	const [originalFundsUsed, setOriginalFundsUsed] = useState([])
	const [fundsRemoved, setFundsRemoved] = useState([])
	const [fundsAdded, setFundsAdded] = useState([])
	const [allFunds, setAllFunds] = useState([])
	const [templateDef, setTemplateDef] = useState({});
	const [notFound, setNotFound] = useState(false);
	const [assetType, setAssetType] = useState({ label: 'Sector', value: 'Sector' })
	const [editMode, setEditMode] = useState(false)
	const [changedParams, setChangedParams] = useState([])
	const [invalidParams, setInvalidParams] = useState({})
	const [manuallyExpandedRows, setManuallyExpandedRows] = useState([]);
	const [actionables, setActionables] = useState(false)
	
	const actionableCategories = Object.keys(actionables)

	const remote = useRemote();
	const storage = useStorage()
	const history = useHistory()
	const { width } = useWindowDimensions();
	const { template: templateId } = useParams()

	useEffect(() => {
		remote.get("/crm/templates/" + templateId).then((fetchedTemplate) => {
			setOriginalTemplate(fetchedTemplate)
			setTemplate(fetchedTemplate)

		}).catch(e => {
			console.error("Caught e ", e);
			setNotFound(true);
		});

		remote.get(`/crm/templates/${templateId}/publish-steps`).then((fetchSelectedActions) => {
			setOriginalSelectedActions(fetchSelectedActions)
			setSelectedPublishActions(fetchSelectedActions)
		});

		remote.get(`/crm/publish-steps`).then((actionables) => {
			let sortedActionables = actionables.sort((a, b) => a.displayOrder - b.displayOrder)
			let categories = {}
			sortedActionables.forEach(action => {
				if (!categories[action.type]) categories[action.type] = []
				categories[action.type].push(action)
			})
			setActionables(categories)
		});
	}, []);


	useEffect(() => {
		if (template && template.template) {
			try {
				let parsedTemplate = JSON.parse(template.template)
				setTemplateDef(parsedTemplate)

				//if template is fund type, fetch selected funds and all options
				if (parsedTemplate && parsedTemplate.ticker && parsedTemplate.ticker.type === 'fund') {
					storage.getOrFetch('/funds/funds')
						.then((funds) => {
							setAllFunds(funds)
							storage.refresh(`/crm/templates/${templateId}/selected-tickers`)
								.then((resp) => {
									const fundsObject = funds.reduce((a, c) => ({ ...a, [c.id]: c }), {});
									const collatedFunds = resp.map((colFund) => {
										if (fundsObject[colFund.sourceId]) {
											return fundsObject[colFund.sourceId];
										}
										else return null;
									}).filter(fund => fund !== null);
									setOriginalFundsUsed(collatedFunds)
									setFundsUsed(collatedFunds)
								})
						})
				}
			} catch (e) {
				console.error(e)
			}
		}
	}, [template])


	function addActionToTemplate(action, isInvalid) {
		let newActions = selectedPublishActions.map(a => ({ ...a })); // Create a deep copy of the array
		let actionIndex = newActions.findIndex(a => a.stepId === action.id);
		if (actionIndex !== -1) {
			newActions[actionIndex].active = !newActions[actionIndex].active;
			setSelectedPublishActions(newActions);
		}
		if (isInvalid) {
			setManuallyExpandedRows([...manuallyExpandedRows, action.id])
			setTimeout(() => {
				setManuallyExpandedRows(manuallyExpandedRows.filter(row => row !== action.id))
			}, 500)
		}
	}

	function removeActionFromTemplate(action) {
		let newActions = selectedPublishActions.map(a => ({ ...a })); // Create a deep copy of the array
		let actionIndex = newActions.findIndex(a => a.stepId === action.id);
		if (actionIndex !== -1) {
			newActions[actionIndex].active = !newActions[actionIndex].active;
			setSelectedPublishActions(newActions);
		}
		if (invalidParams[action.id]) {
			const newInvalidParams = { ...invalidParams };
			delete newInvalidParams[action.id];
			setInvalidParams(newInvalidParams);
		}
	}

	function handleParamsValidation(actionName, isInvalid) {
		if (isInvalid) {
			setInvalidParams({ ...invalidParams, [actionName]: isInvalid });
		} else {
			const newInvalidParams = { ...invalidParams };
			if (newInvalidParams[actionName]) {

				delete newInvalidParams[actionName];
				setInvalidParams(newInvalidParams);
			}
		}
	};

	function goBack() {
		history.push("/admin/templates")
	}

	function updateParams() {
		if (changedParams.length === 0) {
			setOriginalSelectedActions(selectedPublishActions)
		} else {
			remote.put(`crm/templates/${templateId}/publish-steps/configs`, changedParams).then((response) => {
				setChangedParams([])
				setInvalidParams({})
				setOriginalSelectedActions(response)
				setSelectedPublishActions(response)

				// Show warnings if there are any
				if (response && response.length > 0) {
					response.forEach((action) => {
						if (action.message && action.message.length > 0) {
							action.message.forEach((message) => toastWarning(message))
						}
					})
				}
			})
		}
	}

	function saveEdits() {
		setEditMode(false)
		let changedSelections = selectedPublishActions.filter((action, idx) => { return action.active !== originalSelectedActions[idx].active })
		if (changedSelections.length === 0) updateParams()
		else {
			Promise.all(changedSelections.map((action, idx) => {
				const originalAction = originalSelectedActions.find(a => a.stepId === action.stepId)
				if (action.active !== originalAction.active) {
					if (action.active) remote.put(`/crm/templates/${templateId}/publish-steps/${action.stepId}`)
					else remote.delete(`crm/templates/${templateId}/publish-steps/${action.stepId}`)
				};
			})).then(() => {
				updateParams()
			})
		}
		if (fundsUsed !== originalFundsUsed) {
			saveFundsUsed()
		}
	}

	function onParamsChange(stepId, param, value) {
		let newParams = [...changedParams];
		let stepIndex = newParams.findIndex(step => step.stepId === stepId);

		if (stepIndex === -1) {
			newParams.push({ stepId: stepId, [param]: value });
		} else {
			newParams[stepIndex][param] = value;
		}

		setChangedParams(newParams);
	}

	function addFundUsed(fund) {
		let newFundsUsed = [...fundsUsed, fund]
		setFundsUsed(newFundsUsed)
		setFundsAdded([...fundsAdded, fund])
		setFundsRemoved(fundsRemoved.filter(f => f.id !== fund.id))
	}

	function removeFundUsed(fund) {
		let newFundsUsed = fundsUsed.filter(f => f.id !== fund.id)
		setFundsUsed(newFundsUsed)
		setFundsRemoved([...fundsRemoved, fund])
		setFundsAdded(fundsAdded.filter(f => f.id !== fund.id))
	}

	function saveFundsUsed() {
		const addedFundIds = fundsAdded.map(fund => fund.id);
		const removedFundIds = fundsRemoved.map(fund => fund.id);
		const addedFundIdsNumbersMap = new Map(fundsAdded.map(fund => [fund.id, fund.fundNumber]));
		const removedFundIdsNumbersMap = new Map(fundsRemoved.map(fund => [fund.id, fund.fundNumber]))
		const templateId = template.id; // Assuming `template.id` contains the current template ID
		const promises = [];

		if (addedFundIdsNumbersMap.size > 0) {
			promises.push(
				remote.put(`/crm/templates/${templateId}/selected-tickers/bulk`, Object.fromEntries(addedFundIdsNumbersMap))
			);
		}
		if (removedFundIdsNumbersMap.size > 0) {
			promises.push(
				remote.delete(`/crm/templates/${templateId}/selected-tickers/bulk`, Object.fromEntries(removedFundIdsNumbersMap))
			);
		}

		Promise.all(promises)
			.then((responses) => {
				setOriginalFundsUsed(fundsUsed); // Update the original funds to match the current state
			})
			.catch(error => {
				console.error("Error updating funds:", error);
			});
	};

	function cancelEdits() {
		setTemplate(originalTemplate)
		setEditMode(false)
		setSelectedPublishActions(originalSelectedActions)
		setChangedParams([])
		setInvalidParams({})
		setFundsUsed(originalFundsUsed)
	}

	if (notFound) return (
		<Page fixedBanner>
			<PageContentBanner divider>
				<ActionButtonsBanner />
			</PageContentBanner>
			<PageContentBodyGrid id="client-details-page" rowGap="xl" gridTemplateRows="1fr" splitScroll>
				<p>Invalid template</p>
			</PageContentBodyGrid>
		</Page>
	)

	return (
		<Page fixedBanner>
			{/* Page banner with back button */}
			<PageContentBanner divider>
				<ActionButtonsBanner
					handleBackOnClick={goBack}
					editMode={editMode}
					edit={{ onClick: () => setEditMode(true) }}
					cancel={{ onClick: () => { cancelEdits() } }}
					save={{ disabled: Object.keys(invalidParams).length > 0, onClick: () => { saveEdits() } }}
				/>
			</PageContentBanner>
			{/* Page content */}
			<PageContentBodyGrid id="client-details-page" rowGap="xl" gridTemplateRows="1fr" splitScroll>
				{/* grid container for the template details section */}
				<PageGridItem container col="1 / span 6">
					<PageContentBodyGrid rowGap="xl" gridColCount="6" subGrid>
						{/* General Details */}
						<TemplateGeneralDetails template={templateDef} setTemplate={setTemplateDef} editMode={editMode} />
						<PageGridDivider col="1 / span 6" />
						{/* Settings Lists */}
						{actionableCategories.map((category, index) => {
							return <TemplateSettingsList
								key={index + "-template-settng-category"}
								templateId={templateId}
								templateName={template.name}
								data={actionables && actionables[category] ? actionables[category] : []}
								addActionToTemplate={addActionToTemplate}
								removeAction={removeActionFromTemplate}
								selected={selectedPublishActions}
								setSelectedPublishActions={setSelectedPublishActions}
								templateType={templateDef?.ticker?.templateType || "-"}
								editMode={editMode}
								category={category}
								onParamsChange={onParamsChange}
								changedParams={changedParams}
								setInvalidParams={handleParamsValidation}
								invalidParams={invalidParams}
								manuallyExpandedRows={manuallyExpandedRows}
							/>
						})}
					</PageContentBodyGrid>
				</PageGridItem>
				{/* grid container for the sectors list section */}
				<PageGridItem container col="7 / span 6">
					<PageContentBodyGrid showScrollbar rowGap="xl" gridColCount="6" subGrid divider={width < 1200 ? "top" : "left"} paddingBottom={"3rem"}>
						<TemplateOptions template={templateDef} assetType={assetType} setAssetType={setAssetType} />
						{templateDef && templateDef.ticker && templateDef.ticker.options && (templateDef.ticker.type === 'selection' || templateDef.ticker.type === 'scheme') &&
							<>
								<PageGridItem col="1 / span 6">
									<PageGridDivider col="1 / span 6" />
								</PageGridItem>
								<PageGridItem col="1 / span 6">
									<MiniSectorsList data={templateDef.ticker && templateDef.ticker.options} assetType={assetType} stopRedirect />
								</PageGridItem>
							</>
						}
						{templateDef && templateDef.ticker && templateDef.ticker.type === 'fund' &&
							<AssociatedFundsList
								editMode={editMode}
								fundsUsed={fundsUsed}
								addFundUsed={addFundUsed}
								removeFundUsed={removeFundUsed}
								allFunds={allFunds}
							/>
						}
					</PageContentBodyGrid>
				</PageGridItem>
			</PageContentBodyGrid>
		</Page>
	);
}

export default TemplateMgmt;